import PageBackTitle from "../../../Component/PageBackTitle";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../../../../src/Config";
import axios from "axios";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MdAttachFile, MdDeleteOutline } from "react-icons/md";
import { FiUpload } from "react-icons/fi";
import { IoCloseOutline } from "react-icons/io5";

function AddCylinder({ isSidebarExpanded }) {
  const [formData, setFormData] = useState({
    gas_type_id: "",
    concentration_value: "",
    engineering_unit: "",
    status: "",
    serialNumber: "",
    cylinderSize: "",
    location: "",
    expiry_date: "",
    cylinderType: "",
    certificate_no: "",
  });
  const [gasOptions, setGasOptions] = useState([]);
  const { id } = useParams();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const navigate = useNavigate();
  const [detectorTypes, setDetectorTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]); // if you have stored files
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  const statusOptions = [
    { value: "available", label: "Available" },
    { value: "in_use", label: "In Use" },
    { value: "expired", label: "Expired" },
    { value: "empty", label: "Empty" },
  ];

  const unitOptions = [
    { value: "ppm", label: "ppm" },
    { value: "%", label: "%" },
    { value: "mg/m³", label: "mg/m³" },
  ];

  const options = [
    { value: "cylinder", label: "Cylinder" },
    { value: "canister", label: "Canister" },
    { value: "disposable", label: "Disposable Cylinder" },
    { value: "refillable", label: "Refillable Cylinder" },
  ];

  const handleChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      cylinderType: selectedOption ? selectedOption.value : "",
    }));

    // Clear error if valid
    if (selectedOption && selectedOption.value.trim()) {
      setErrors((prev) => ({
        ...prev,
        cylinderType: "",
      }));
    }
  };

  useEffect(() => {
    const fetchGases = async () => {
      try {
        const authKey = localStorage.getItem("authKey");
        const response = await axios.get(
          `${API_URL}/api/get_calibration_gas_types`,
          {
            headers: { Authorization: authKey },
          }
        );

        const options = response.data.calibration_gas_types.map((gas) => ({
          label: gas.gas_name,
          value: gas.id, // or gas.gas_name if you prefer
          fullData: gas,
        }));

        setGasOptions(options);
      } catch (error) {
        console.error("Error fetching gases:", error);
      }
    };

    fetchGases();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.gas_type_id) {
      errors.gas_type_id = "Gas type is required.";
    }

    if (!formData.concentration_value.trim()) {
      errors.concentration_value = "Concentration value is required.";
    }

    if (!formData.engineering_unit.trim()) {
      errors.engineering_unit = "Engineering unit is required.";
    }

    if (!formData.status) {
      errors.status = "Status is required.";
    }

    if (!formData.serialNumber.trim()) {
      errors.serialNumber = "Serial number is required.";
    }

    if (!formData.cylinderSize.trim()) {
      errors.cylinderSize = "Cylinder size is required.";
    }
    if (!formData.cylinderType.trim()) {
      errors.cylinderType = "Cylinder type is required.";
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formatDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    const payload = {
      gas_type_id: formData.gas_type_id,
      concentration: formData.concentration,
      concentration_value: formData.concentration_value,
      total_volume: formData.total_volume,
      expiry_date: formatDate(formData.expiry_date),
      location: formData.location,
      status: formData.status,
      serial_number: formData.serialNumber,
      cylinder_size: formData.cylinderSize,
      concentration_unit: formData.engineering_unit, // ✅ correct key
      cylinder_type: formData.cylinderType,
      certificate_number: formData.certificate_no,
    };

    const formDataToSend = new FormData();

    // Add all text fields
    Object.entries(payload).forEach(([key, value]) => {
      formDataToSend.append(key, value);
    });

    // ✅ Add files ONLY if you have any
    if (selectedFiles.length > 0) {
      selectedFiles.forEach((file) => {
        formDataToSend.append("attachments", file);
      });
    }

    const authKey = localStorage.getItem("authKey");

    try {
      const response = await axios.put(
        `${API_URL}/api/update_gas_cylinder/${id}`,
        formDataToSend,
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setSnackbarMessage("Gas Cylinder updated successfully!");
        setSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        setSnackbarMessage("Failed to add gas detector type.");
        setSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage("Something went wrong.");
      setSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSelectChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";

    setFormData((prev) => ({
      ...prev,
      type_id: value,
    }));

    // Clear error if a value is selected
    if (value) {
      // setFormErrors((prevErrors) => ({
      //   ...prevErrors,
      //   type_id: "",
      // }));
    }
  };

  const handleStatusChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      status: selectedOption?.value || "",
    }));
    if (selectedOption) {
      // setFormErrors((prevErrors) => ({
      //   ...prevErrors,
      //   status: "",
      // }));
    }
  };

  useEffect(() => {
    const authKey = localStorage.getItem("authKey");

    axios
      .get(`${API_URL}/api/get_gas_detector_types`, {
        headers: {
          Authorization: authKey,
        },
      })
      .then((res) => {
        const options = res.data.gas_detector_types.map((item) => ({
          value: item.id,
          label: item.gas_name,
        }));
        setDetectorTypes(options);
      })
      .catch((err) => console.error("Error fetching detector types:", err));
  }, []);

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      expiry_date: date,
    }));

    // Clear error if a valid date is selected
  };

  useEffect(() => {
    const fetchCylinderData = async () => {
      if (!id) return;
      try {
        const authKey = localStorage.getItem("authKey");
        const res = await axios.get(`${API_URL}/api/get_gas_cylinder/${id}`, {
          headers: {
            Authorization: authKey,
          },
        });

        const data = res.data;

        setFormData({
          gas_type_id: data.gas_type_id || "",
          concentration_value: data.concentration_value || "",
          engineering_unit: data.concentration_unit || "",
          status: data.status || "",
          serialNumber: data.serial_number || "",
          cylinderSize: data.cylinder_size || "",
          location: data.location || "",
          expiry_date: data.expiry_date ? new Date(data.expiry_date) : null,
          cylinderType: data.cylinder_type,
          certificate_no: data.certificate_number,
        });

        if (Array.isArray(data.attachments) && data.attachments.length > 0) {
          setExistingFiles(data.attachments);
        }

        console.log("data", data);
        console.log("data?.ManualWorkPermits?.attachments", data?.attachments);
      } catch (err) {
        console.error("Error fetching cylinder details:", err);
      }
    };

    fetchCylinderData();
  }, [id]);

  const handleUploadButtonClick = () => {
    fileInputRef.current.click();
    setErrors((prev) => ({ ...prev, attachments: "" }));
  };

  //   const handleFileInputChange = (files) => {
  //     const fileArray = Array.from(files);
  //     setSelectedFiles(fileArray);
  //     setErrors((prev) => ({ ...prev, attachments: "" }));
  //   };

  const handleFileInputChange = (files) => {
    const fileArray = Array.from(files).slice(0, 1); // Only 1 file
    setSelectedFiles(fileArray);
    setExistingFiles([]); // clear old
    setErrors((prev) => ({ ...prev, attachments: "" }));
  };

  const handleDeleteFile = (index) => {
    setSelectedFiles([]); // only one file, so clear all
    setIsPopupOpen(false);
  };

  const handleIconClick = () => {
    setIsPopupOpen((prev) => !prev);
  };

  const handleCloseAttachment = () => {
    setIsPopupOpen(false);
  };

  const handleDeleteExistingFile = (id) => {
    setExistingFiles((prev) => prev.filter((file) => file.id !== id));
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">Edit Gas Cylinder</PageBackTitle>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            <form>
              {/* Name Field */}
              <div className="form-group-select">
                <label>
                  Calibration Gas<span className="text-danger">*</span>
                </label>
                <Select
                  name="gas_type"
                  options={gasOptions}
                  isClearable
                  value={gasOptions.find(
                    (opt) => opt.value === formData.gas_type_id
                  )}
                  onChange={(selected) => {
                    setFormData((prev) => ({
                      ...prev,
                      gas_type_id: selected?.value || "",
                      gas_type: selected?.label || "",
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      gas_type_id: "",
                    }));
                  }}
                  placeholder="Select Calibration Gas"
                />
                {errors.gas_type_id && (
                  <div className="error-message">{errors.gas_type_id}</div>
                )}
              </div>
              <div className="form-group-select">
                <label>
                  Cylinder Type<span className="text-danger">*</span>
                </label>
                <Select
                  options={options}
                  value={options.find(
                    (option) => option.value === formData.cylinderType
                  )}
                  name="cylinderType"
                  onChange={handleChange}
                  placeholder="Select Cylinder Type"
                  isClearable
                />
                {errors.cylinderType && (
                  <div className="error-message">{errors.cylinderType}</div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="DepartmentName">
                  Concentration Value<span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type="number"
                    name="concentration_value"
                    value={formData.concentration_value}
                    onChange={handleInputChange}
                    placeholder="Enter Concentration Value"
                  />
                </div>
                {errors.concentration_value && (
                  <div className="error-message">
                    {errors.concentration_value}
                  </div>
                )}
              </div>
              <div className="form-group-select">
                <label htmlFor="engineering_unit">
                  Concentration Unit <span className="text-danger">*</span>
                </label>
                <Select
                  name="engineering_unit"
                  options={unitOptions}
                  value={unitOptions.find(
                    (opt) => opt.value === formData.engineering_unit
                  )}
                  onChange={(selectedOption) =>
                    handleInputChange({
                      target: {
                        name: "engineering_unit",
                        value: selectedOption.value,
                      },
                    })
                  }
                  placeholder="Select Unit"
                />

                {errors.engineering_unit && (
                  <div className="error-message">{errors.engineering_unit}</div>
                )}
              </div>

              <div className="form-group-select">
                <label>
                  Status <span className="text-danger">*</span>
                </label>
                <Select
                  options={statusOptions}
                  value={statusOptions.find(
                    (option) => option.value === formData.status
                  )}
                  onChange={handleStatusChange}
                  placeholder="Select status"
                />
                {errors.status && (
                  <div className="error-message">{errors.status}</div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="DepartmentName">
                  Serial Number <span className="text-danger">*</span>
                </label>{" "}
                <div className="input-group">
                  <input
                    type="text"
                    name="serialNumber" // ✅ correct casing
                    value={formData.serialNumber}
                    onChange={handleInputChange}
                    placeholder="Enter Serial Number"
                  />
                </div>
                {errors.serialNumber && (
                  <div className="error-message">{errors.serialNumber}</div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="DepartmentName">
                  Cylinder Size <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type="number"
                    name="cylinderSize"
                    value={formData.cylinderSize}
                    onChange={handleInputChange}
                    placeholder="Enter Cylinder Size"
                  />
                </div>
                {errors.cylinderSize && (
                  <div className="error-message">{errors.cylinderSize}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="certificate_no">Certificate No</label>
                <div className="input-group">
                  <input
                    type="text"
                    name="certificate_no"
                    value={formData.certificate_no}
                    onChange={handleInputChange}
                    placeholder="Enter certificate number"
                  />
                </div>
              </div>

              <div className="form-group-attachments">
                <label htmlFor="attachments">Certificate Document</label>

                {/* Upload Button */}
                <button
                  type="button"
                  className="challan-btn"
                  onClick={handleUploadButtonClick}
                >
                  <FiUpload /> Upload File
                </button>

                {/* Hidden File Input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileInputChange(e.target.files)}
                  style={{ display: "none" }}
                />

                {/* File Count Badge - shows either new or old */}
                {(selectedFiles.length > 0 || existingFiles.length > 0) && (
                  <span
                    className="inquiry-file-btn"
                    onClick={handleIconClick}
                    ref={buttonRef}
                  >
                    <MdAttachFile className="inquiry-file-icon" />{" "}
                    {selectedFiles.length > 0
                      ? selectedFiles.length
                      : existingFiles.length}
                  </span>
                )}

                {/* Attachment Popup */}
                {isPopupOpen && (
                  <div className="attach-popup-inward" ref={containerRef}>
                    <div className="attach-popup-contant">
                      <div className="attachment-header">
                        <div className="attachment-header-content">
                          Attachments
                        </div>
                        <button
                          className="fiile-close-btn"
                          onClick={handleCloseAttachment}
                        >
                          <IoCloseOutline className="file-close-icon" />
                        </button>
                      </div>

                      {/* NEW FILE PREVIEW */}
                      {selectedFiles.length > 0 && (
                        <div className="attach-scrroll-body">
                          {selectedFiles.map((file, index) => (
                            <div
                              className="attach-popup-contant-body"
                              key={index}
                            >
                              <div className="" style={{ color: "black" }}>
                                <div className="attach-file-show">
                                  <div className="" style={{ color: "black" }}>
                                    {file.name}
                                  </div>
                                  <div
                                    className="text-attachment-file"
                                    style={{ color: "black" }}
                                  >
                                    File Size: {(file.size / 1024).toFixed(2)}{" "}
                                    KB
                                  </div>
                                </div>
                                <div
                                  className="file-delete-btn"
                                  onClick={() => handleDeleteFile(index)}
                                >
                                  <MdDeleteOutline className="file-delete-icon" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* EXISTING FILE PREVIEW */}
                      {selectedFiles.length === 0 &&
                        existingFiles.length > 0 && (
                          <div className="attach-scrroll-body">
                            {existingFiles.map((file) => (
                              <div
                                className="attach-popup-contant-body"
                                key={file.id}
                              >
                                <div className="" style={{ color: "black" }}>
                                  <div
                                    className="attach-file-show"
                                    style={{ color: "black" }}
                                  >
                                    <a
                                      href={`${API_URL}/${file.file_path}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className=""
                                      style={{ color: "black" }}
                                    >
                                      {file.filename}
                                    </a>
                                    <div
                                      className="text-attachment-file"
                                      style={{ color: "black" }}
                                    >
                                      File Size:{" "}
                                      {(file.file_size / 1024).toFixed(2)} KB
                                    </div>
                                  </div>
                                  <div
                                    className="file-delete-btn"
                                    onClick={() =>
                                      handleDeleteExistingFile(file.id)
                                    }
                                  >
                                    <MdDeleteOutline className="file-delete-icon" />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="DepartmentName">Location</label>
                <div className="input-group">
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Enter Location"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Expiration Date</label>
                <div className="input-group">
                  <DatePicker
                    selected={formData.expiry_date}
                    onChange={handleDateChange}
                    placeholderText="Select Expiration date"
                    dateFormat="yyyy-MM-dd"
                    // className={`form-control ${
                    //   formErrors.installation_date ? "error-input" : ""
                    // }`}
                  />
                </div>
              </div>
            </form>
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="submit"
                onClick={handleSubmit}
              >
                Save
              </button>
              <button
                type="button"
                className="data-form-cancel-button"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={severity}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default AddCylinder;
